import {
  Checkbox,
  theme,
  Button,
  Empty,
  Tooltip as AntdTooltip,
  Switch,
} from "antd";
import {
  AppstoreOutlined,
  FontSizeOutlined,
  LoadingOutlined,
  NumberOutlined,
  SwapOutlined,
} from "@ant-design/icons";
import { useMemo, useRef, useCallback } from "react";
import { useEffectOnce } from "usehooks-ts";
import { Flex } from "src/components/Flex";
import { Hexagrid } from "src/components/Hexagrid";
import { useSubscribeEffect } from "src/hooks/useSubscribeEffect";
import { DynamicCanvas } from "src/components/DynamicCanvas";
import { sortSetNatural } from "src/utils/sort";
import { FilterMenu } from "src/search/components/inputs/FilterMenu";
import { usePopupContext } from "src/hooks/usePopup";
import { asKey } from "src/utils/fields";
import { CHAR_SIZE, labelRotate, labelSize } from "src/utils/labels";
import { formatValue, unitTypeForCol } from "src/utils/strings";
import { InvalidGroupBys } from "./InvalidGroupBys";
import { VisualizationProps } from "./visualization-props";
import { ContentView, ContentViews } from "./useContentView";
import { valueFromRecord, valueToLabel } from "./common";
import {
  CompareWithConfig,
  CompareWithControls,
  toCompareWithConfig,
} from "./CompareWithControls";
import { ColorRampConfig, ColorRampPicker } from "./ColorRampPicker";
import { toColorRampConfig } from "src/utils/colors-v2";
import { SearchContext } from "src/search/useSearchContext";
import { EmptyResultExplanation } from "./EmptyResultExplanation";
import { InvalidAggregations } from "./InvalidAggregations";
import { useColorScheme } from "src/hooks/useColorScheme";
import {
  getConsistentColorPalette,
  getContrastTextColor,
  ColorTheme,
  getColorScale,
} from "src/utils/colors-v2";
import chroma from "chroma-js";
import { HeatMapCellTooltip } from "./tooltips/HeatMapCellTooltip";
import { RampTheme } from "src/utils/color";

// Helper function to map ColorTheme to RampTheme for Hexagrid compatibility
function colorThemeToRampTheme(colorTheme: ColorTheme): RampTheme {
  switch (colorTheme) {
    case ColorTheme.OBSERVABLE_10:
    case ColorTheme.OBSERVABLE_20: {
      return RampTheme.classic;
    }
    case ColorTheme.ACCESSIBLE:
    case ColorTheme.ACCESSIBLE_PRO: {
      return RampTheme.accessible;
    }
    case ColorTheme.CORPORATE: {
      return RampTheme.presentation;
    }
    case ColorTheme.MODERN: {
      return RampTheme.material;
    }
    case ColorTheme.COLORBREWER_SET1:
    case ColorTheme.COLORBREWER_SET2: {
      return RampTheme.qualitative;
    }
    case ColorTheme.BLUES: {
      return RampTheme.sequential_blue;
    }
    case ColorTheme.VIRIDIS:
    case ColorTheme.PLASMA: {
      return RampTheme.sequential_multi;
    }
    case ColorTheme.RED_BLUE: {
      return RampTheme.diverging_rb;
    }
    case ColorTheme.BROWN_GREEN: {
      return RampTheme.diverging_bg;
    }
    default: {
      return RampTheme.classic;
    }
  }
}

// A good test link for when you make changes.
// Make sure this still loads and renders in a timely manner
// http://0.0.0.0:8000/45fbf84a-8e8d-4062-b015-205fdb1cace5/explore?d=%7B%22saved%22%3A%228298a29b-48f2-4e93-b13a-e781b00e18a4%22%2C%22title%22%3A%22Selecting%20%5C%22Event%5C%22%22%7D&s=eyJvcmdJZCI6IjQ1ZmJmODRhLThlOGQtNDA2Mi1iMDE1LTIwNWZkYjFjYWNlNSIsInNlbGVjdCI6WyJFdmVudCJdLCJmaWx0ZXJzIjpbXSwic2VhcmNoIjoiIiwiZHVyYXRpb24iOiJQMzBEIiwiZ3JvdXBCeXMiOlt7InRhcmdldCI6IkV2ZW50IiwiYXR0cmlidXRlIjoiRGF5In0seyJ0YXJnZXQiOiJFdmVudCIsImF0dHJpYnV0ZSI6Iklzc3VlS2V5In1dLCJhZ2dyZWdhdGlvbiI6eyJ0YXJnZXQiOiJJc3N1ZSIsImF0dHJpYnV0ZSI6InV1aWQiLCJyZWR1Y2VyIjoiY291bnQifX0%3D&t=%7B%22tabs%22%3A%5B%7B%22id%22%3A%228298a29b-48f2-4e93-b13a-e781b00e18a4%22%2C%22title%22%3A%22Selecting%20%5C%22Event%5C%22%22%7D%5D%2C%22activeTab%22%3A%228298a29b-48f2-4e93-b13a-e781b00e18a4%22%2C%22fullscreen%22%3Afalse%7D&v=%7B%22value%22%3A%22heatMap%22%2C%22config%22%3A%7B%22hex%22%3Afalse%2C%22labels%22%3Atrue%2C%22values%22%3Afalse%7D%7D

// There is a bit math in here that can get hard (for Joey) to follow.
// Some constants might help...
// const LABEL_SIZE = 100;
const MIN_CELL_SIZE = 20;

type Config = ColorRampConfig &
  CompareWithConfig & {
    hex: boolean;
    labels: boolean;
    legend: boolean;
    values: boolean;
    invertAxes: boolean;
    invertComparisonColors: boolean;
  };

export function HeatMap({ help, context, onRefuse }: VisualizationProps) {
  useEffectOnce(() => {
    context.view.setConfig(toConfig(context.view.config));
    context.aggregations.setWindow({});
  });
  const config = useConfig(context.view.config);
  useSubscribeEffect(context.aggregations);

  if (
    context.params.selectors.countGroupBys(context.params.state) < 1 ||
    context.params.selectors.countGroupBys(context.params.state) > 2
  )
    return onRefuse ? (
      <Refused onRefuse={onRefuse} />
    ) : (
      <InvalidGroupBys
        numGroups={context.params.selectors.countGroupBys(context.params.state)}
        view={context.view}
        requiredGroups={{ min: 1, max: 2 }}
      />
    );

  if (context.params.selectors.countAggregations(context.params.state) > 1)
    return onRefuse ? (
      <Refused onRefuse={onRefuse} />
    ) : (
      <InvalidAggregations
        numAggregations={context.params.selectors.countAggregations(
          context.params.state
        )}
        view={context.view}
        minAggregations={1}
        maxAggregations={1}
      />
    );
  return <Ready config={config} context={context} />;
}

function Refused({ onRefuse }: { onRefuse(): void }) {
  useEffectOnce(() => onRefuse());
  return null;
}

function Ready({
  context,
  config,
}: { config: Config } & Pick<VisualizationProps, "context">) {
  if (context.aggregations.data?.data?.length === 0) {
    const explanation = context.aggregations.data?.meta?.explanation || [];
    return <EmptyResultExplanation explanation={explanation} />;
  }

  return (
    <DynamicCanvas>
      {({ width, height }) =>
        config.hex ? (
          <HeatMapHex
            height={height}
            width={width}
            context={context}
            {...config}
          />
        ) : (
          <HeatMapGrid
            width={width}
            height={height}
            context={context}
            {...config}
          />
        )
      }
    </DynamicCanvas>
  );
}

export function HeatMapGrid(props: {
  invertAxes: boolean;
  labels: boolean;
  legend: boolean;
  values: boolean;
  width: number;
  height: number;
  showChangeAs: Config["showChangeAs"];
  theme: ColorTheme;
  invertComparisonColors: boolean;
  context: SearchContext & { view: ContentView; title: string };
}) {
  const [width, height] = [props.width, props.height - (props.legend ? 30 : 0)];
  const grid = useHeatMapGrid({
    context: props.context,
    height,
    invertAxes: props.invertAxes,
    labels: props.labels,
    showChangeAs: props.showChangeAs,
    values: props.values,
    width,
  });
  const columnInfo = useMemo(
    () => props.context.aggregations.data?.meta?.columnInfo || [],
    [props.context.aggregations.data?.meta?.columnInfo]
  );
  const aggField = useMemo(
    () =>
      props.context.params.state.aggregations?.at(0) ||
      columnInfo.find((c) => c.reducer),
    [columnInfo, props.context.params.state.aggregations]
  );

  const popup = usePopupContext();
  // a little sleight of hand to prevent flicker
  const gridRef = useRef(grid);
  if (!props.context.aggregations.isLoading) gridRef.current = grid;
  const {
    table,
    xs,
    ys,
    xCol,
    yCol,
    zCol,
    zMin,
    zMax,
    xLabelSize,
    rise,
    angle,
    yLabelSize,
    cellHeight,
    cellWidth,
  } = gridRef.current;

  const xType = unitTypeForCol(xCol, columnInfo);
  const yType = unitTypeForCol(yCol, columnInfo);
  const zType = unitTypeForCol(zCol, columnInfo);
  const { token } = theme.useToken();

  const hasCompareWith =
    props.context.params.state.compareWith?.method === "tuple";
  const mode = useColorScheme();
  const colors = useCallback(
    (value?: string | number) => {
      // Use color scale from the centralized color system
      const colorScale = getColorScale(props.theme);

      // Return semi-transparent background color for undefined/null values
      if (value === undefined || value === "__VZBL::null__") {
        return token.colorBgContainer + "22";
      }

      // Ensure we have a valid numeric value
      const numValue = typeof value === "string" ? Number(value) : value;

      if (Number.isNaN(numValue)) {
        return token.colorBgContainer + "22";
      }

      // Calculate normalized value (0-1 range)
      const normalizedValue =
        zMax > zMin ? (numValue - zMin) / (zMax - zMin) : 0.5;
      const clampedValue = Math.max(0, Math.min(1, normalizedValue));

      return hasCompareWith && props.invertComparisonColors
        ? colorScale(1 - clampedValue).hex()
        : colorScale(clampedValue).hex();
    },
    [
      hasCompareWith,
      props.theme,
      props.invertComparisonColors,
      zMin,
      zMax,
      token.colorBgContainer,
    ]
  );
  const expectedWidth = cellWidth * xs.size + yLabelSize;
  const expectedHeight = cellHeight * ys.size + rise;
  if (Number.isNaN(expectedHeight) || Number.isNaN(expectedWidth))
    return props.context.aggregations.isLoading ? (
      <Flex centered style={{ height: "100%" }}>
        <LoadingOutlined />
      </Flex>
    ) : (
      <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} />
    );
  return (
    <div
      style={{
        width: props.width,
        height: props.height,
        overflow: "auto",
        position: "relative",
        paddingTop: (height - expectedHeight) / 2,
        paddingLeft: (width - expectedWidth) / 2,
      }}
    >
      {props.context.aggregations.isLoading && (
        <Flex
          centered
          style={{
            position: "absolute",
            top: 0,
            left: 0,
            width: "100%",
            height: "100%",
            zIndex: 1,
            backgroundColor: token.colorBgContainer,
          }}
        >
          <LoadingOutlined />
        </Flex>
      )}
      <Flex start style={{ width: expectedWidth }}>
        {props.labels && (
          <div style={{ width: yLabelSize }}>
            {[...ys].map((y_) => (
              <div
                key={y_}
                style={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "flex-end",
                  height: cellHeight,
                  width: yLabelSize,
                }}
              >
                <div
                  title={formatValue(y_, yType.type, yType.unit)}
                  style={{
                    fontSize: 12,
                    fontWeight: 400,
                    width: "100%",
                    textAlign: "right",
                    padding: CHAR_SIZE[12] / 2,
                    paddingLeft: 0,
                    borderRadius: 3,
                  }}
                  className="trunc text-hover"
                  onClick={(e) => {
                    if (gridRef.current.yField)
                      popup.setPopup(
                        e,
                        <FilterMenu
                          values={[
                            {
                              value: y_,
                              label: formatValue(y_, yType.type, yType.unit),
                            },
                          ]}
                          properties={[gridRef.current.yField]}
                          context={props.context}
                        />
                      );
                  }}
                >
                  {valueToLabel(formatValue(y_, yType.type, yType.unit))}
                </div>
              </div>
            ))}
          </div>
        )}
        <div style={{ flex: 1 }}>
          {[...ys].map((y_) => (
            <Flex start key={y_} style={{ height: cellHeight }}>
              {[...xs].map((x_) => {
                const cellData = table[x_][y_];
                return (
                  <HeatMapCellTooltip
                    key={`${x_}.${y_}`}
                    cell={{
                      x: formatValue(x_, xType.type, xType.unit),
                      y: formatValue(y_, yType.type, yType.unit),
                      z: cellData
                        ? formatValue(
                            cellData.z,
                            zType.type,
                            zType.tuple && props.showChangeAs === "percentage"
                              ? "%"
                              : zType.unit
                          )
                        : "",
                      xField: gridRef.current.xField,
                      yField: gridRef.current.yField,
                      xType,
                      yType,
                      zType,
                      measureValue: cellData
                        ? {
                            label: asKey(aggField!),
                            value: cellData.z,
                          }
                        : undefined,
                    }}
                    context={props.context}
                  >
                    <div
                      className={cellData === undefined ? "" : "saturate-hover"}
                      style={{
                        display: "flex",
                        justifyContent: "center",
                        alignItems: "center",
                        cursor:
                          cellData &&
                          ![undefined, "__VZBL::null__"].includes(cellData.z)
                            ? "pointer"
                            : "default",
                        backgroundColor: `${colors(cellData?.z)}`,
                        borderRadius: Math.max(
                          2,
                          Math.min(
                            Math.min(cellHeight, cellWidth) - MIN_CELL_SIZE,
                            6
                          )
                        ),
                        height: cellHeight,
                        width: cellWidth,
                        textAlign: "center",
                        userSelect: "none",
                        border: `solid 1px ${token.colorBgContainer}`,
                        boxSizing: "border-box",
                      }}
                      onClick={(e) => {
                        const { xField, yField } = gridRef.current;
                        if (
                          xField &&
                          yField &&
                          cellData &&
                          cellData.z !== undefined &&
                          cellData.z !== "__VZBL::null__"
                        )
                          popup.setPopup(
                            e,
                            <FilterMenu
                              accentColor={colors(cellData.z)}
                              values={[
                                {
                                  value: x_,
                                  label: formatValue(
                                    x_,
                                    xType.type,
                                    xType.unit
                                  ),
                                },
                                {
                                  value: y_,
                                  label: formatValue(
                                    y_,
                                    yType.type,
                                    yType.unit
                                  ),
                                },
                              ]}
                              properties={[xField, yField]}
                              context={props.context}
                              measureValue={{
                                label: asKey(aggField!),
                                value: cellData.z || "-",
                              }}
                            />
                          );
                      }}
                    >
                      <span
                        className="trunc"
                        style={{
                          padding: Math.max(1, Math.min(4, cellWidth * 0.05)),
                          fontSize: Math.max(8, Math.min(12, cellWidth / 6)),
                          lineHeight: `${Math.max(
                            10,
                            Math.min(16, cellHeight * 0.7)
                          )}px`,
                          fontWeight: 400,
                          letterSpacing: 0.1,
                          color:
                            props.values &&
                            cellData?.z !== undefined &&
                            cellData?.z !== "__VZBL::null__"
                              ? getContrastTextColor(colors(cellData.z))
                              : "transparent",
                          maxWidth: "100%",
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                          whiteSpace: "nowrap",
                          display: "block",
                        }}
                      >
                        {cellData?.z === undefined ||
                        cellData?.z === "__VZBL::null__"
                          ? ""
                          : hasCompareWith
                          ? `${formatValue(
                              cellData.originalValue,
                              zType.type,
                              zType.unit
                            )} (${formatValue(
                              cellData.comparisonValue,
                              zType.type,
                              props.showChangeAs === "percentage"
                                ? "%"
                                : zType.unit
                            )})`
                          : formatValue(
                              cellData.z,
                              zType.type,
                              zType.tuple && props.showChangeAs === "percentage"
                                ? "%"
                                : zType.unit
                            )}
                      </span>
                    </div>
                  </HeatMapCellTooltip>
                );
              })}
            </Flex>
          ))}
          {props.legend && (
            <div
              style={{
                height: 20,
                marginTop: 2,
                overflow: "auto",
                borderRadius: 2,
                padding: 2,
                backgroundColor: colors(),
              }}
            >
              <Flex
                spaceBetween
                gap={4}
                style={{
                  borderRadius: 2,
                  fontSize: 12,
                  padding: "1px 4px",
                  color: token.colorTextLabel,
                }}
              >
                <span>
                  {formatValue(
                    zMin,
                    zType.type,
                    zType.tuple && props.showChangeAs === "percentage"
                      ? "%"
                      : zType.unit
                  )}
                </span>
                <div
                  style={{
                    flex: 1,
                    height: 5,
                    backgroundImage: `linear-gradient(to right, ${colors(
                      zMin
                    )} 0%, ${
                      zMin < 0 && 0 < zMax
                        ? colors(-0.001) + " " + zeroPercent(zMin, zMax) + "%,"
                        : ""
                    } ${
                      zMin < 0 && 0 < zMax
                        ? colors(0.001) + " " + zeroPercent(zMin, zMax) + "%,"
                        : ""
                    } ${colors(zMax)} 100%)`,
                  }}
                />
                <span>
                  {formatValue(
                    zMax,
                    zType.type,
                    zType.tuple && props.showChangeAs === "percentage"
                      ? "%"
                      : zType.unit
                  )}
                </span>
              </Flex>
            </div>
          )}
          {props.labels && (
            <Flex
              start
              style={{
                height: rise,
                overflow: "hidden",
                position: "relative",
                left: `-${yLabelSize}px`,
                width: `calc(100% + ${yLabelSize}px)`,
                justifyContent: "flex-end",
              }}
            >
              {[...xs].map((x_) => (
                <Flex
                  key={x_}
                  style={{
                    width: cellWidth,
                    height: rise,
                    overflow: "visible",
                  }}
                >
                  <div
                    title={valueToLabel(
                      formatValue(x_, xType.type, xType.unit)
                    )}
                    style={{
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                      whiteSpace: "nowrap",
                      width: xLabelSize,
                      minWidth: xLabelSize,
                      height: Math.min(cellWidth, yLabelSize),
                      maxHeight: Math.min(cellWidth, yLabelSize),
                      lineHeight: `${Math.min(cellWidth, yLabelSize)}px`,
                      fontSize: 12,
                      fontWeight: 400,
                      rotate: `${angle}deg`,
                      position: "relative",
                      left: angle === 0 ? 0 : -CHAR_SIZE[12],
                      textAlign: angle === 0 ? "center" : "right",
                      padding: `0 ${CHAR_SIZE[12] / 2}px`,
                      borderRadius: 2,
                    }}
                    className="trunc text-hover"
                    onClick={(e) => {
                      if (gridRef.current.xField)
                        popup.setPopup(
                          e,
                          <FilterMenu
                            context={props.context}
                            values={[
                              {
                                label: formatValue(x_, xType.type, xType.unit),
                                value: x_,
                              },
                            ]}
                            properties={[gridRef.current.xField]}
                          />
                        );
                    }}
                  >
                    {valueToLabel(formatValue(x_, xType.type, xType.unit))}
                  </div>
                </Flex>
              ))}
            </Flex>
          )}
        </div>
      </Flex>
    </div>
  );
}

function HeatMapHex(props: {
  height: number;
  width: number;
  values: boolean;
  legend: Config["legend"];
  showChangeAs: Config["showChangeAs"];
  invertComparisonColors: Config["invertComparisonColors"];
  theme: Config["theme"];
  context: SearchContext & { view: ContentView; title: string };
}) {
  const { aggregations, params } = props.context;
  const keys = Object.keys((aggregations.data?.data || [])[0] || {});
  const [count, ...combos] = keys.reverse();
  combos.reverse();

  const columnInfo = useMemo(
    () => props.context.aggregations.data?.meta?.columnInfo || [],
    [props.context.aggregations.data?.meta?.columnInfo]
  );

  const comboTypes = useMemo(
    () => combos.map((c) => unitTypeForCol(c, columnInfo)),
    [combos, columnInfo]
  );

  const types = useMemo(
    () =>
      keys.reduce(
        (acc, curr) => ((acc[curr] = unitTypeForCol(curr, columnInfo)), acc),
        {} as Record<string, ReturnType<typeof unitTypeForCol>>
      ),
    [keys, columnInfo]
  );
  const asPercent = props.showChangeAs === "percentage";
  const hasCompareWith = params.state.compareWith?.method === "tuple";
  const data = useMemo(
    () =>
      (aggregations.data?.data || []).map((row) => {
        const valueObj = valueFromRecord(row, count, columnInfo);
        const valueStr = hasCompareWith
          ? (asPercent ? valueObj.percent : valueObj.delta) + ""
          : valueObj.value;
        const value = {
          value: valueStr,
          label: formatValue(
            valueStr,
            types[count].type,
            types[count].tuple && asPercent ? "%" : types[count].unit
          ),
        };
        return {
          label: combos
            .map((col, i) =>
              formatValue(
                valueFromRecord(row, col, columnInfo).value,
                comboTypes[i].type,
                comboTypes[i].tuple && asPercent ? "%" : comboTypes[i].unit
              )
            )
            .join(", "),
          values: combos.map((col) => ({
            value: valueFromRecord(row, col, columnInfo).value,
            label: formatValue(
              valueFromRecord(row, col, columnInfo).value,
              types[col].type,
              types[col].tuple && asPercent ? "%" : types[col].unit
            ),
          })),
          value,
        };
      }),
    [
      aggregations.data?.data,
      asPercent,
      columnInfo,
      comboTypes,
      combos,
      count,
      hasCompareWith,
      types,
    ]
  );
  const groupBys = params.state.groupBys || [];
  const area = (props.width - 32) * (props.height - (props.legend ? 64 : 32));
  const size = Math.floor(Math.sqrt(area / data.length) * 0.75);
  return (
    <div
      style={{
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
        width: "100%",
        height: "100%",
      }}
    >
      <Hexagrid
        size={size}
        context={props.context}
        data={data}
        showValue={props.values}
        properties={groupBys}
        legend={props.legend}
        onDrillDown={() => props.context.view.set(ContentViews.default)}
        invertComparisonColors={props.invertComparisonColors}
        hueReverse={false}
        hueShift={0}
        isPastel={false}
        isMuted={false}
        theme={colorThemeToRampTheme(props.theme)}
      />
    </div>
  );
}

export function HeatMapControls(props: {
  context: SearchContext & { view: ContentView; title: string };
}) {
  const { view, params } = props.context;
  const config = useConfig(view.config);
  const { token } = theme.useToken();
  const hasCompareWith = params.state.compareWith?.method === "tuple";
  return (
    <>
      <div className="section">
        <div className="section-header">Layout</div>
        <div className="layout-control-grid">
          <AntdTooltip title="Tiles">
            <Button
              type="default"
              onClick={() => view.setConfig({ ...config, hex: false })}
              className={`layout-control-button ${
                config.hex ? "" : "selected"
              }`}
            >
              <AppstoreOutlined />
              <span className="layout-control-button-label">Tiles</span>
            </Button>
          </AntdTooltip>
          <AntdTooltip title="Hexagon Tiles">
            <Button
              type="default"
              onClick={() => view.setConfig({ ...config, hex: !config.hex })}
              className={`layout-control-button ${
                config.hex ? "selected" : ""
              }`}
            >
              <svg
                className="layout-control-button-icon"
                viewBox="0 0 24 24"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  d="M12 4L20 8V16L12 20L4 16V8L12 4Z"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
              <span className="layout-control-button-label">Hexagon</span>
            </Button>
          </AntdTooltip>
        </div>
        <Flex spaceBetween style={{ width: "100%" }}>
          <span
            style={{
              fontSize: "14px",
              color: token.colorText,
            }}
          >
            <SwapOutlined /> Invert Axis
          </span>
          <Switch
            checked={config.invertAxes}
            onChange={(checked) =>
              view.setConfig({ ...config, invertAxes: checked })
            }
            size="small"
          />
        </Flex>
        <div className="section">
          <div className="section-header">Style</div>
          <div className="layout-control-grid">
            <AntdTooltip title="Show Labels">
              <Button
                type="default"
                disabled={config.hex}
                onClick={() =>
                  view.setConfig({ ...config, labels: !config.labels })
                }
                className={`layout-control-button ${
                  config.labels ? "selected" : ""
                }`}
              >
                <FontSizeOutlined />
                <span className="layout-control-button-label">Labels</span>
              </Button>
            </AntdTooltip>
            <AntdTooltip title="Show Legend">
              <Button
                type="default"
                onClick={() =>
                  view.setConfig({ ...config, legend: !config.legend })
                }
                className={`layout-control-button ${
                  config.legend ? "selected" : ""
                }`}
              >
                <svg
                  className="layout-control-button-icon"
                  viewBox="0 0 24 24"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <rect
                    x="4"
                    y="6"
                    width="3"
                    height="3"
                    rx="0.5"
                    fill="currentColor"
                  />
                  <rect
                    x="4"
                    y="11"
                    width="3"
                    height="3"
                    rx="0.5"
                    fill="currentColor"
                  />
                  <rect
                    x="4"
                    y="16"
                    width="3"
                    height="3"
                    rx="0.5"
                    fill="currentColor"
                  />
                  <path
                    d="M9 7.5h11M9 12.5h11M9 17.5h11"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                  />
                </svg>
                <span className="layout-control-button-label">Legend</span>
              </Button>
            </AntdTooltip>
            <AntdTooltip title="Show Values">
              <Button
                type="default"
                onClick={() =>
                  view.setConfig({ ...config, values: !config.values })
                }
                className={`layout-control-button ${
                  config.values ? "selected" : ""
                }`}
              >
                <NumberOutlined />
                <span className="layout-control-button-label">Values</span>
              </Button>
            </AntdTooltip>
          </div>
          <ColorRampPicker
            ramp={config.theme}
            onChangeRamp={(theme) => view.setConfig({ ...config, theme })}
          />
        </div>
      </div>
    </>
  );
}

export function useHeatMapGrid(props: {
  invertAxes: boolean;
  labels: boolean;
  values: boolean;
  width: number;
  height: number;
  showChangeAs: Config["showChangeAs"];
  context: SearchContext & { view: ContentView; title: string };
}) {
  const groupBys = props.context.params.state.groupBys || [];
  const xField = groupBys.at(0);
  const yField = groupBys.at(1);

  const columnInfo = useMemo(
    () => props.context.aggregations.data?.meta?.columnInfo || [],
    [props.context.aggregations.data?.meta?.columnInfo]
  );
  const aggData = useMemo(
    () => props.context.aggregations.data?.data || [],
    [props.context.aggregations.data?.data]
  );
  const dataKeys = Object.keys(aggData[0] || {});
  const [x, y, z] = props.invertAxes
    ? [dataKeys[1], dataKeys[0], dataKeys[2]]
    : dataKeys;
  const hasCompareClause =
    props.context.params.state.compareWith?.method === "tuple";
  const { xs, ys, zMin, zMax, table } = useMemo(() => {
    interface CellData {
      x: string;
      y: string;
      z: string;
      originalValue: string;
      comparisonValue: string;
    }

    const table: Record<string, Record<string, CellData>> = {};
    const xData: string[] = [];
    const yData: string[] = [];
    let zMin = Number.MAX_SAFE_INTEGER;
    let zMax = Number.MIN_SAFE_INTEGER;

    aggData.forEach((d, index) => {
      const xV = valueFromRecord(d, x, columnInfo);
      const yV = valueFromRecord(d, y, columnInfo);
      const zV = valueFromRecord(d, z, columnInfo);

      const x_ =
        hasCompareClause &&
        props.showChangeAs === "value" &&
        xV.delta !== undefined
          ? xV.delta
          : hasCompareClause &&
            props.showChangeAs === "percentage" &&
            xV.percent !== undefined
          ? xV.percent
          : xV.value;

      const y_ =
        hasCompareClause &&
        props.showChangeAs === "value" &&
        yV.delta !== undefined
          ? yV.delta
          : hasCompareClause &&
            props.showChangeAs === "percentage" &&
            yV.percent !== undefined
          ? yV.percent
          : yV.value;

      const z_ =
        hasCompareClause &&
        props.showChangeAs === "value" &&
        zV.delta !== undefined
          ? zV.delta
          : hasCompareClause &&
            props.showChangeAs === "percentage" &&
            zV.percent !== undefined
          ? zV.percent
          : zV.value;

      const zNumVal = Number(z_);

      zMin = Math.min(zMin, Number.isNaN(zNumVal) ? zMin : zNumVal);
      zMax = Math.max(zMax, Number.isNaN(zNumVal) ? zMax : zNumVal);

      xData.push(x_);
      yData.push(y_);

      const cell: CellData = {
        x: x_,
        y: y_,
        z: z_ || "__VZBL::null__",
        originalValue: zV.value || "__VZBL::null__",
        comparisonValue:
          (hasCompareClause
            ? props.showChangeAs === "percentage"
              ? zV.percent
              : zV.delta
            : zV.value) || "__VZBL::null__",
      };

      if (table[x_]) table[x_][y_] = cell;
      else table[x_] = { [y_]: cell };
    });

    const xs = sortSetNatural(new Set(xData));
    const ys = sortSetNatural(new Set(yData));
    return { xs, ys, zMin, zMax, table };
  }, [x, y, z, aggData, hasCompareClause, props.showChangeAs, columnInfo]);

  const xType = unitTypeForCol(x, columnInfo);
  const yType = unitTypeForCol(y, columnInfo);

  const xLabelSize = props.labels
    ? Math.min(
        labelSize([...xs].map((x_) => formatValue(x_, xType.type, xType.unit))),
        100
      ) + CHAR_SIZE[12]
    : 0;
  const yLabelSize =
    (props.labels
      ? Math.min(
          labelSize(
            [...ys].map((y_) => formatValue(y_, yType.type, yType.unit))
          ),
          100
        ) + CHAR_SIZE[12]
      : 0) + 10;
  const cellWidth = Math.max(
    (props.width - yLabelSize) / xs.size,
    MIN_CELL_SIZE
  );
  const { angle, rise } = labelRotate(xLabelSize, cellWidth);
  const cellHeight = Math.max((props.height - rise) / ys.size, MIN_CELL_SIZE);

  return {
    table,
    xs,
    ys,
    xField: props.invertAxes ? yField : xField,
    yField: props.invertAxes ? xField : yField,
    xCol: x,
    yCol: y,
    zCol: z,
    zMin,
    zMax,
    cellWidth,
    cellHeight,
    xLabelSize,
    yLabelSize,
    angle,
    rise,
  };
}

const toConfig: (data: Config | any) => Config = (data) => ({
  ...toCompareWithConfig(data),
  ...toColorRampConfig(data),
  hex: typeof data?.hex === "boolean" ? data.hex : false,
  labels: typeof data?.labels === "boolean" ? data.labels : true,
  legend: typeof data?.legend === "boolean" ? data.legend : false,
  values: typeof data?.values === "boolean" ? data.values : true,
  invertAxes: typeof data?.invertAxes === "boolean" ? data.invertAxes : false,
  invertComparisonColors:
    typeof data?.invertComparisonColors === "boolean"
      ? data.invertComparisonColors
      : false,
});

const useConfig = (data: any) => toConfig(data);

function zeroPercent(min: number, max: number) {
  return ((0 - min) / (max - min)) * 100;
}
